import { useLayoutEffect, useRef, useState } from "react";
import type { SpeakerRole } from "@shared/ocenaTypy.js";
import { api, type MowcaDto } from "../../lib/api.js";
import {
  bladZmianyMowcy,
  MAX_ZNAKOW_NAZWY_MOWCY,
  normalizujNazwe,
  rolaMowcy,
  type BladZmianyMowcy,
} from "../../lib/podzial.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { Dialog } from "../ui/Dialog.js";
import { useToast } from "../ui/Toast.js";
import { EdytorMowcy } from "./EdytorMowcy.js";

/**
 * Okno zmiany nazwy i roli jednego mowcy (D-1) - dla analizy, ktora ma juz transkrypt: nagranie
 * i tekst po podziale. Zapisuje od razu (PATCH), bo zmiana nazwy nie wymaga ponownej oceny.
 *
 * Stan pol zyje tutaj, a nie w polach: przyciski dialogu stoja poza jego trescia, wiec musza
 * widziec to, co wpisano. Przy kazdym otwarciu pola startuja od aktualnych danych mowcy.
 */
export function DialogMowcy({
  otwarty,
  sesjaId,
  mowca,
  pozostaliMowcy,
  onZamknij,
  onZapisano,
}: {
  otwarty: boolean;
  sesjaId: string;
  mowca: MowcaDto | null;
  /** Pozostali mowcy tej sesji: nazwa nie moze sie powtarzac (serwer odrzuca to samo kodem 409). */
  pozostaliMowcy: readonly MowcaDto[];
  onZamknij: () => void;
  /** Wywolane po zapisie, zanim okno sie zamknie: widok pobiera sesje jeszcze raz. */
  onZapisano: () => void | Promise<void>;
}): React.JSX.Element {
  const toast = useToast();
  const nazwaRef = useRef<HTMLInputElement>(null);
  const [nazwa, setNazwa] = useState("");
  const [rola, setRola] = useState<SpeakerRole>("INNY");
  const [blad, setBlad] = useState<BladZmianyMowcy | null>(null);
  const [zajety, setZajety] = useState(false);

  // Kazde otwarcie zaczyna od danych mowcy z serwera, a nie od tego, co zostalo po poprzednim.
  // useLayoutEffect, zeby pola nie mignely starymi wartosciami w pierwszej klatce po otwarciu.
  useLayoutEffect(() => {
    if (!otwarty || !mowca) return;
    setNazwa(mowca.displayName ?? "");
    setRola(rolaMowcy(mowca.role));
    setBlad(null);
    setZajety(false);
  }, [otwarty, mowca]);

  async function zapisz(): Promise<void> {
    if (!mowca || zajety) return;

    const nowaNazwa = normalizujNazwe(nazwa);
    if (nowaNazwa.length > MAX_ZNAKOW_NAZWY_MOWCY) {
      setBlad({ pole: "nazwa", tekst: `Nazwa może mieć najwyżej ${MAX_ZNAKOW_NAZWY_MOWCY} znaków.` });
      return;
    }
    // Pusta nazwa wraca do etykiety, wiec do porownania bierzemy to, co zostanie na ekranie.
    const pokazana = nowaNazwa.length > 0 ? nowaNazwa : mowca.label;
    const zajeta = pozostaliMowcy.find(
      (m) => (m.displayName ?? m.label).toLocaleLowerCase("pl") === pokazana.toLocaleLowerCase("pl"),
    );
    if (zajeta) {
      setBlad({ pole: "nazwa", tekst: `Inny mówca ma już nazwę ${pokazana}. Wybierz inną.` });
      return;
    }

    const patch: { displayName?: string | null; role?: SpeakerRole } = {};
    if (nowaNazwa !== (mowca.displayName ?? "")) patch.displayName = nowaNazwa.length > 0 ? nowaNazwa : null;
    if (rola !== rolaMowcy(mowca.role)) patch.role = rola;

    // Nic do zapisania: zamykamy bez zapytania do serwera (serwer odpowiedzialby brak_zmian).
    if (Object.keys(patch).length === 0) {
      onZamknij();
      return;
    }

    setZajety(true);
    setBlad(null);
    try {
      await api.zmienMowce(sesjaId, mowca.id, patch);
      // TO-1: krotkie potwierdzenie ZDARZENIA (CP-6: krotko i konkretnie).
      toast.sukces("Zapisano zmiany.");
      await onZapisano();
      onZamknij();
    } catch (err) {
      setBlad(bladZmianyMowcy(err));
    } finally {
      setZajety(false);
    }
  }

  return (
    <Dialog
      otwarty={otwarty}
      onZamknij={zajety ? () => undefined : onZamknij}
      tytul={mowca ? `Nazwa i rola: ${mowca.displayName ?? mowca.label}` : "Nazwa i rola mówcy"}
      poczatkowyFokusRef={nazwaRef}
      akcje={
        <>
          <Button wariant="secondary" onClick={onZamknij} wylaczony={zajety}>
            Anuluj
          </Button>
          <Button wariant="primary" zajety={zajety} onClick={() => void zapisz()}>
            Zapisz zmiany
          </Button>
        </>
      }
    >
      {otwarty && mowca && (
        <>
          <p className="field-hint">
            Zmiana nazwy nie wymaga ponownej oceny: nowa nazwa pojawi się w transkrypcie i w raporcie.
          </p>
          <EdytorMowcy
            idBazowy={`mowca-${mowca.id}`}
            etykieta={mowca.label}
            nazwa={nazwa}
            rola={rola}
            onNazwa={(n) => {
              setNazwa(n);
              setBlad(null);
            }}
            onRola={(r) => {
              setRola(r);
              setBlad(null);
            }}
            blad={blad?.pole === "nazwa" ? blad.tekst : undefined}
            wylaczony={zajety}
            nazwaRef={nazwaRef}
          />
          {(blad?.pole === "rola" || blad?.pole === "ogolny") && (
            <p className="field-error" role="alert">
              <IconOstrzezenie size={14} />
              {blad.tekst}
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
